import { useState } from "react";
import $api from "@/zapi-frontend/shared/api/axios";
import { TDocumentStatus } from "@/zapi-frontend/shared/types/shared";

export type TPreUploadedDocument = {
  documentId: number;
  fileName: string;
  status: TDocumentStatus;
  fileSize?: number;
  createdAt?: Date;
  webUrl?: string;
};

export type TMinFile = {
  name: string;
  size: number;
  id: number;
  status: TDocumentStatus;
  isDeleting: boolean;
  isUploading: boolean;
  webUrl?: string;
};

type TPreUploadResponse = {
  data: {
    documentId: number;
    fileName: string;
    status: TDocumentStatus;
  };
};

type TPreUploadWebLinkResponse = {
  data: {
    documentId: number;
    webUrl: string;
    status: TDocumentStatus;
  };
};

export const preUploadDocument = async (file: File): Promise<TPreUploadedDocument> => {
  const response = await $api.post<TPreUploadResponse>(
    "/projects/documents/pre-upload",
    { file },
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return {
    documentId: response.data.data.documentId,
    fileName: response.data.data.fileName,
    status: response.data.data.status
  };
};

export const preUploadWebLink = async (url: string): Promise<TPreUploadedDocument> => {
  const response = await $api.post<TPreUploadWebLinkResponse>("/projects/documents/pre-upload-url", { url });
  return {
    documentId: response.data.data.documentId,
    fileName: response.data.data.webUrl,
    status: response.data.data.status,
    webUrl: response.data.data.webUrl
  };
};

export const usePreUploadDocuments = () => {
  const [uploadedDocuments, setUploadedDocuments] = useState<TPreUploadedDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const uploadDocuments = async (files: File[]): Promise<TMinFile[]> => {
    setIsUploading(true);
    const minFiles: TMinFile[] = [];

    try {
      const uploadPromises = files.map(async (file) => {
        try {
          const doc = await preUploadDocument(file);
          setUploadedDocuments((prev) => [...prev, doc]);

          const minFile: TMinFile = {
            name: doc.fileName,
            size: file.size,
            id: doc.documentId,
            status: doc.status,
            isDeleting: false,
            isUploading: false
          };

          minFiles.push(minFile);
          return minFile;
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          throw error;
        }
      });

      await Promise.all(uploadPromises);
      return minFiles;
    } finally {
      setIsUploading(false);
    }
  };

  const addWebLink = async (url: string): Promise<TMinFile> => {
    setIsUploading(true);
    try {
      const doc = await preUploadWebLink(url);
      setUploadedDocuments((prev) => [...prev, doc]);

      const minFile: TMinFile = {
        name: doc.webUrl || url,
        size: 0,
        id: doc.documentId,
        status: doc.status,
        isDeleting: false,
        isUploading: false,
        webUrl: doc.webUrl || url
      };

      return minFile;
    } finally {
      setIsUploading(false);
    }
  };

  const clearDocuments = () => {
    setUploadedDocuments([]);
  };

  return {
    uploadedDocuments,
    isUploading,
    uploadDocuments,
    addWebLink,
    clearDocuments
  };
};
